import { useState } from 'react';
import { CalendarPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

function dateOnly(day: Date): string {
  const month = String(day.getMonth() + 1).padStart(2, '0');
  const date = String(day.getDate()).padStart(2, '0');
  return `${day.getFullYear()}-${month}-${date}`;
}

function addDays(day: Date, days: number): Date {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate() + days);
}

/** Today, tomorrow, this Friday (when it is still ahead) and next Monday. */
export function quickDueDates(today: Date): Array<{ label: string; value: string }> {
  const weekday = today.getDay();
  const toFriday = (5 - weekday + 7) % 7;
  const toMonday = ((1 - weekday + 7) % 7) || 7;
  const picks = [
    { label: 'Today', value: dateOnly(today) },
    { label: 'Tomorrow', value: dateOnly(addDays(today, 1)) },
  ];
  if (toFriday > 1) picks.push({ label: 'Friday', value: dateOnly(addDays(today, toFriday)) });
  if (toMonday > 1) picks.push({ label: 'Next Monday', value: dateOnly(addDays(today, toMonday)) });
  return picks;
}

/** A quiet "Set due date" action for rows with no date, with quick picks and a date field. */
export default function SetDueDate({
  today,
  label,
  onSet,
}: {
  today: Date;
  /** Names the task for screen readers. */
  label: string;
  onSet: (dueDate: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState('');
  const [saving, setSaving] = useState(false);

  async function save(value: string) {
    if (!value) return;
    setSaving(true);
    try {
      await onSet(value);
      setOpen(false);
      setPicked('');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Set due date for ${label}`}
          className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-sm font-medium text-primary/85 transition-colors hover:bg-primary/5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <CalendarPlus className="h-4 w-4" aria-hidden />
          <span className="hidden sm:inline">Set due date</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-60 p-3">
        <p className="mb-2 text-xs font-medium text-muted-foreground">Due</p>
        <div className="grid grid-cols-2 gap-1.5">
          {quickDueDates(today).map((pick) => (
            <Button
              key={pick.label}
              type="button"
              variant="outline"
              size="sm"
              disabled={saving}
              onClick={() => void save(pick.value)}
            >
              {pick.label}
            </Button>
          ))}
        </div>
        <form
          className="mt-3 flex items-center gap-1.5"
          onSubmit={(event) => {
            event.preventDefault();
            void save(picked);
          }}
        >
          <Input
            type="date"
            aria-label="Pick a date"
            value={picked}
            min={dateOnly(today)}
            onChange={(event) => setPicked(event.target.value)}
            className="h-8 text-sm"
          />
          <Button type="submit" size="sm" disabled={saving || !picked}>
            Set
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}
